<script setup>
import MainHeader from "@/Layouts/MainHeader.vue";
import Answer from "@/Layouts/Answer.vue";
import MainFooter from "@/Layouts/MainFooter.vue";
import Tracking from '@/Components/ResourseLoader.vue';
import ShareButtons from "@/Components/ShareButtons.vue";
import AccordionComment from "@/Components/AccordionComment.vue";
import CitySet from "@/Components/CitySet.vue";
import { Head, Link } from "@inertiajs/vue3";
import { router } from '@inertiajs/vue3';
import { useModal } from "vue-final-modal";
import { reactive, ref, computed, onMounted, onUnmounted } from "vue";
import axios from 'axios';

let set = defineProps({
  question: Object,
  answers: Object,
  authid: Number,
  uslugi: Array,
  auth: Object,
  city: Object,
  backendurl: String,
  aiPending: Boolean,
  aiLawyerIds: Array,
  offersCount: { type: Number, default: 0 },
  lawyersCalled: { type: Boolean, default: false },
});

let cntedt = ref(false);
let newBody = ref(false);
let showChecklist = ref(false);

// Typing indicator phases: 'looking' → 'typing'
const typingPhase = ref('looking');
let answerPoll = null;
onMounted(() => {
  if (set.aiPending && !topAnswers.value.length) {
    setTimeout(() => { typingPhase.value = 'typing'; }, 4000);
    // Пока юрист «печатает» и ответов ещё нет — опрашиваем сервер, чтобы первый ответ
    // появился у наблюдающего автора сам, без ручной перезагрузки (раньше был один
    // reload через 35с, и индикатор замирал, если ответ ещё не опубликован).
    let tries = 0;
    answerPoll = setInterval(() => {
      if (topAnswers.value.length || tries++ >= 30) {
        clearInterval(answerPoll);
        answerPoll = null;
        return;
      }
      router.reload({ only: ['answers', 'aiPending'] });
    }, 10000);
  }
});
onUnmounted(() => { if (answerPoll) clearInterval(answerPoll); });

let usluga_id = 0;
if (set.question.usluga != null) {
  usluga_id = set.question.usluga;
}

let form = reactive({
  id: set.question.id,
  body: set.question.abody,
  usluga: usluga_id,
});

const topAnswers = computed(() => set.answers.filter(a => !a.parent_comment_id));

// CTA «позвать других юристов» — только автору, после первого ответа.
const isAuthor = computed(() => set.authid && set.authid === set.question.user_id);
const inviteReady = computed(() => !!set.question.cities_url && !!set.question.uslugis_url);
const inviteLink = computed(() =>
  route('offer.main', [set.question.cities_url, set.question.uslugis_url]) + '?invite=' + set.question.url
);
const lawyersCalledState = ref(!!set.lawyersCalled);
const callBusy = ref(false);
const callMsg = ref('');
const callLawyers = () => {
  if (callBusy.value || lawyersCalledState.value) return;
  callBusy.value = true;
  axios.post(route('questions.callLawyers', set.question.id))
    .then(() => { lawyersCalledState.value = true; callMsg.value = 'Юристы приглашены. Загляните на сайт позже — как появятся ответы, мы пришлём их вам на почту.'; })
    .catch(() => { callMsg.value = 'Не удалось отправить приглашения, попробуйте позже.'; })
    .finally(() => { callBusy.value = false; });
};


const setUsluga = () => {
  router.post(route("questions.setusl", [set.question.id]), form, {
    preserveScroll: true,
  });
};

const update = () => {
  if (cntedt.value == true) {
    router.post(
      route("questions.update"),
      { id: set.question.id, body: newBody.value.innerText },
      { preserveScroll: true }
    );
  }
};

const childRef = ref(null);

// Модалка выбора региона ИМЕННО для вопроса (привязка к questions.city),
// отдельно от городового пикёра в шапке, который меняет город пользователя.
const { open: openQuestionCity, close: closeQuestionCity } = useModal({
  component: CitySet,
  attrs: {
    questionUrl: set.question.url,
    onConfirm() { closeQuestionCity(); },
  },
});

// Процент готовности — ровно по 4 видимым пунктам чек-листа ниже, чтобы «всё
// отмечено» давало 100%. (Раньше считался один раз при загрузке, плюс учитывал
// скрытый 5-й критерий «получен ответ» без галочки → при всех галочках выходило 80%.)
const checklistItems = computed(() => [
  !!set.question.uslugis_usl_name,   // 1. категория
  !!set.question.cities_title,       // 2. регион
  !!set.question.phone,              // 3. профиль
  !!set.question.user_share,         // 4. поделились
]);
const checklistDone = computed(() => checklistItems.value.filter(Boolean).length);
const percent = computed(() => Math.round((checklistDone.value / checklistItems.value.length) * 100));
const message = computed(() => {
  if (percent.value === 100) return "Отлично, Ваш вопрос самый лучший";
  if (percent.value >= 60) return "Вы почти у цели";
  if (percent.value >= 40) return "хорошо, но можно лучше";
  return "вопрос нужно дополнить";
});
</script>

<template>
  <Head>
    <title>Вопрос юристу - {{ set.question.header }}</title>
    <meta name="description" :content="set.question.abody.substr(0, 260) + '...'" />
    <meta property="og:title" :content="set.question.header" />
    <meta property="og:type" content="website" />
    <meta property="og:url" :content="'https://nedicom.ru/questions/' + set.question.url" />
    <meta property="og:image" content="https://nedicom.ru/logoq.webp" />
  </Head>

  <div class="min-h-screen bg-gray-50">
    <MainHeader :auth="set.auth" :city="set.city" ref="childRef" />
    <!-- Ориентир main: без него программа чтения с экрана не умеет перепрыгнуть шапку к содержимому. Классы повторяют flex-контекст корня, чтобы дети остались flex-элементами. -->
    <main class="flex flex-col flex-1">

    <!-- QAPage-разметку эмитим только при наличии ответов (иначе Question без answer невалиден) -->
    <div :itemscope="topAnswers.length ? '' : null" :itemtype="topAnswers.length ? 'https://schema.org/QAPage' : null">
      <div class="max-w-xl mx-auto px-4 py-6">

        <!-- Breadcrumb -->
        <div class="text-xs text-gray-500 mb-4 flex items-center gap-1 min-w-0">
          <a href="/questions" class="hover:underline flex-shrink-0">Вопросы юристу онлайн</a>
          <span class="flex-shrink-0">·</span>
          <span class="truncate text-gray-600">{{ set.question.header }}</span>
        </div>

        <!-- Thread card -->
        <div class="bg-white rounded-2xl border border-gray-100 overflow-hidden"
             :itemprop="topAnswers.length ? 'mainEntity' : null"
             :itemscope="topAnswers.length ? '' : null"
             :itemtype="topAnswers.length ? 'https://schema.org/Question' : null">
          <meta v-if="topAnswers.length" itemprop="answerCount" :content="topAnswers.length" />
          <meta itemprop="datePublished" :content="set.question.chema_created_at" />
          <meta v-if="set.question.user_like" itemprop="upvoteCount"
                :content="set.question.user_like + set.question.user_bookmark + set.question.user_share + set.question.comment_count" />

          <!-- Root post: question -->
          <div class="flex gap-3 px-4 pt-4 pb-3">
            <!-- Avatar + thread line -->
            <div class="flex flex-col items-center w-10 flex-shrink-0">
              <img v-if="set.question.avatar_path"
                   :src="'https://nedicom.ru/' + set.question.avatar_path"
                   width="40" height="40"
                   class="w-10 h-10 rounded-full object-cover" />
              <div v-else class="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                <svg class="w-5 h-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"/>
                </svg>
              </div>
              <div v-if="topAnswers.length" class="w-px bg-gray-200 flex-1 mt-2 min-h-4"></div>
            </div>

            <!-- Content -->
            <div class="flex-1 min-w-0 pb-3">
              <div class="flex items-start justify-between gap-2 mb-1">
                <span itemprop="author" itemscope itemtype="https://schema.org/Person">
                  <span itemprop="name" class="font-semibold text-sm text-gray-900">{{ set.question.name || 'Пользователь' }}</span>
                </span>
                <div class="flex items-center gap-2 flex-shrink-0">
                  <span class="text-xs text-gray-500">{{ set.question.created_at }}</span>
                  <svg v-if="authid === 1 || (authid === set.question.user_id && !topAnswers.length)"
                       @click="router.post(route('questions.delete', [set.question.id]))"
                       class="w-4 h-4 text-gray-300 hover:text-red-400 cursor-pointer"
                       xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M5 7h14m-9 3v8m4-8v8M10 3h4a1 1 0 0 1 1 1v3H9V4a1 1 0 0 1 1-1ZM6 7h12v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V7Z"/>
                  </svg>
                </div>
              </div>

              <!-- Edit controls -->
              <div v-if="set.auth && (set.auth.id == 1 || set.auth.id == set.question.user_id)" class="mb-2">
                <button v-if="!cntedt" type="button" @click="cntedt = true"
                        class="text-xs text-indigo-600 hover:underline">редактировать</button>
                <div v-else class="flex gap-2 mb-2">
                  <button type="button" @click="update(), (cntedt = false)"
                          class="text-xs bg-green-600 text-white px-3 py-1 rounded-full">сохранить</button>
                  <button type="button" @click="(cntedt = false), (newBody.innerText = set.question.abody)"
                          class="text-xs bg-red-500 text-white px-3 py-1 rounded-full">отменить</button>
                </div>
              </div>

              <h1 itemprop="name" class="font-bold text-base text-gray-900 mb-2 leading-snug">
                {{ set.question.header }}
              </h1>
              <p itemprop="text" ref="newBody" :contenteditable="cntedt"
                 class="text-sm text-gray-800 leading-relaxed"
                 :class="{ 'border border-indigo-400 rounded-lg p-2': cntedt }">
                {{ set.question.abody }}
              </p>

              <!-- Actions -->
              <div class="flex items-center gap-3 mt-3">
                <a href="#comment" class="flex items-center gap-1 text-gray-500 hover:text-gray-600 text-xs">
                  <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M9 17h6l3 3v-3h2V9h-2M4 4h11v8H9l-3 3v-3H4V4Z"/>
                  </svg>
                  <span>{{ topAnswers.length }}</span>
                </a>
                <ShareButtons :bundle="set.question" :auth="set.auth" />
              </div>
            </div>
          </div>

          <!-- Typing indicator -->
          <div v-if="set.aiPending && !topAnswers.length"
               class="border-t border-gray-100 px-4 py-3 flex items-center gap-3">
            <!-- Generic lawyer avatar -->
            <div class="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
              <svg class="w-4 h-4 text-indigo-500" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"/>
              </svg>
            </div>
            <div>
              <p class="text-xs font-medium text-gray-700">Дежурный юрист</p>
              <transition name="fade" mode="out-in">
                <span v-if="typingPhase === 'looking'" key="looking" class="text-xs text-gray-500">просматривает вопрос...</span>
                <span v-else key="typing" class="text-xs text-indigo-600 flex items-center gap-1">
                  печатает ответ
                  <span class="flex gap-0.5">
                    <span class="w-1 h-1 rounded-full bg-indigo-400 animate-bounce" style="animation-delay:0ms"></span>
                    <span class="w-1 h-1 rounded-full bg-indigo-400 animate-bounce" style="animation-delay:150ms"></span>
                    <span class="w-1 h-1 rounded-full bg-indigo-400 animate-bounce" style="animation-delay:300ms"></span>
                  </span>
                </span>
              </transition>
            </div>
          </div>

          <!-- Thread replies: answers -->
          <template v-if="topAnswers.length">
            <div v-for="(answer, index) in topAnswers" :key="answer.id"
                 :id="answer.id"
                 class="border-t border-gray-100 flex gap-3 px-4 py-3"
                 itemprop="suggestedAnswer" itemscope itemtype="https://schema.org/Answer">
              <meta itemprop="datePublished" :content="answer.updated_at" />
              <meta itemprop="url" :content="'https://nedicom.ru/questions/' + set.question.url + '#comment'" />
              <meta itemprop="upvoteCount" :content="set.question.user_like || 1" />

              <!-- Avatar + thread line -->
              <div class="flex flex-col items-center w-10 flex-shrink-0">
                <img :src="'https://nedicom.ru/' + answer.user_ans.avatar_path"
                     width="40" height="40"
                     class="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                <div v-if="index < topAnswers.length - 1" class="w-px bg-gray-200 flex-1 mt-2"></div>
              </div>

              <!-- Content -->
              <div class="flex-1 min-w-0 pb-1">
                <div class="flex items-start justify-between gap-2 mb-1">
                  <Link v-if="answer.user_ans.lawyer == 1"
                        :href="route('lawyer', answer.user_ans.id)"
                        class="flex items-center gap-1.5 hover:underline min-w-0"
                        itemprop="author" itemscope itemtype="https://schema.org/Person">
                    <meta itemprop="url" :content="'https://nedicom.ru/lawyers/' + answer.user_ans.id" />
                    <span itemprop="name" class="font-semibold text-sm text-indigo-700 truncate">{{ answer.user_ans.name }}</span>
                    <span class="text-xs bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded-full font-medium flex-shrink-0">юрист</span>
                  </Link>
                  <span v-else class="font-semibold text-sm text-gray-700">
                    пользователь скрыт
                  </span>
                  <div class="flex items-center gap-2 flex-shrink-0">
                    <span class="text-xs text-gray-500">{{ answer.created_at }}</span>
                    <svg v-if="authid === 1"
                         @click="router.post(route('answer.delete', [answer.id]))"
                         class="w-4 h-4 text-gray-300 hover:text-red-400 cursor-pointer"
                         xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M5 7h14m-9 3v8m4-8v8M10 3h4a1 1 0 0 1 1 1v3H9V4a1 1 0 0 1 1-1ZM6 7h12v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V7Z"/>
                    </svg>
                  </div>
                </div>

                <p itemprop="text" class="text-sm text-gray-800 leading-relaxed">{{ answer.body }}</p>

                <!-- Subcomments -->
                <div v-if="answer.subcomments && answer.subcomments.length" class="mt-2 space-y-2">
                  <div v-for="sub in answer.subcomments" :key="sub.id"
                       class="ml-3 border-l-2 border-gray-100 pl-3">
                    <div class="flex items-center gap-2 mb-0.5 flex-wrap">
                      <img :src="'https://nedicom.ru/' + sub.avatar_path" class="w-5 h-5 rounded-full flex-shrink-0" />
                      <span class="text-xs font-semibold"
                            :class="sub.lawyer == 1 ? 'text-indigo-700' : 'text-gray-700'">
                        {{ sub.lawyer == 1 ? sub.name : 'пользователь скрыт' }}
                      </span>
                      <span class="text-xs text-gray-500">{{ sub.pivot.created_at }}</span>
                      <svg v-if="authid === 1"
                           @click="router.post(route('answer.delete', [sub.pivot.id]))"
                           class="w-3.5 h-3.5 text-gray-300 hover:text-red-400 cursor-pointer ml-auto"
                           xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M5 7h14m-9 3v8m4-8v8M10 3h4a1 1 0 0 1 1 1v3H9V4a1 1 0 0 1 1-1ZM6 7h12v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V7Z"/>
                      </svg>
                    </div>
                    <p class="text-xs text-gray-700 leading-relaxed">{{ sub.pivot.body }}</p>
                  </div>
                </div>

                <!-- Reply accordion -->
                <AccordionComment
                  :question="set.question"
                  :answerid="answer.id"
                  :authid="set.authid"
                  type="question"
                  :article_id="null" />
              </div>
            </div>
          </template>

          <!-- No answers: only show to non-authors -->
          <div v-else-if="authid !== set.question.user_id"
               class="border-t border-gray-100 py-8 text-center" id="comment">
            <p class="text-sm text-gray-500">Будьте первым — ответьте на этот вопрос</p>
          </div>
        </div>

        <!-- CTA: позвать других юристов (только автору, после первого ответа) -->
        <div v-if="isAuthor && topAnswers.length"
             class="mt-3 rounded-2xl border border-indigo-100 bg-indigo-50 px-4 py-4">
          <p class="text-sm font-semibold text-indigo-900">Хотите больше мнений по вашему вопросу?</p>
          <p class="text-xs text-indigo-700 mt-1 leading-relaxed">
            Пригласите практикующих юристов<span v-if="set.question.uslugis_usl_name"> по теме «{{ set.question.uslugis_usl_name }}»</span><span v-if="set.question.cities_title"> в городе {{ set.question.cities_title }}</span> ответить на ваш вопрос.
          </p>

          <template v-if="inviteReady">
            <!-- есть объявления → ведём на страницу выбора юристов -->
            <a v-if="set.offersCount > 0" :href="inviteLink"
               class="mt-3 inline-flex items-center gap-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2.5 transition-colors">
              Позвать других юристов
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </a>

            <!-- объявлений в этом городе нет → массовая рассылка юристам категории -->
            <template v-else>
              <button v-if="!lawyersCalledState" type="button" @click="callLawyers" :disabled="callBusy"
                class="mt-3 inline-flex items-center gap-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2.5 transition-colors disabled:opacity-60">
                {{ callBusy ? 'Отправляем…' : 'Позвать юристов' }}
              </button>
              <p v-if="callMsg" class="mt-2 text-xs text-indigo-800">{{ callMsg }}</p>
              <p v-else-if="lawyersCalledState" class="mt-2 text-xs text-indigo-800">
                Юристы приглашены. Загляните на сайт позже — как появятся ответы, мы пришлём их вам на почту.
              </p>
            </template>
          </template>

          <!-- не хватает города/категории — кнопка неактивна -->
          <p v-else class="mt-3 text-xs text-indigo-600">
            Чтобы позвать юристов, укажите категорию и регион вопроса в блоке «Улучшить вопрос» ниже.
          </p>
        </div>

        <!-- Category link — прячем у автора, когда виден CTA «Хотите больше мнений» (дубль) -->
        <div v-if="set.question.uslugis_url && !(isAuthor && topAnswers.length)" class="mt-3 text-center text-sm text-gray-500">
          Найти юриста по категории —
          <a :href="route('offer.main', [
               set.question.cities_url ? set.question.cities_url : 'all-cities',
               set.question.uslugis_url,
             ])"
             class="text-indigo-600 hover:underline font-medium">
            <span v-if="set.question.uslugis_usl_name">{{ set.question.uslugis_usl_name }}</span>
            <span v-if="set.question.cities_title"> · {{ set.question.cities_title }}</span>
          </a>
        </div>

        <!-- Ссылка на хаб города (анкор «Юрист в городе …») — перелинковка под гео-запрос -->
        <div v-if="set.question.cities_url && set.question.cities_title" class="mt-2 text-center text-sm text-gray-500">
          Все юристы города —
          <a :href="'https://nedicom.ru/uslugi/' + set.question.cities_url"
             class="text-indigo-600 hover:underline font-medium">Юрист в городе {{ set.question.cities_title }}</a>
        </div>


        <!-- Answer form -->
        <div class="mt-4 bg-white rounded-2xl border border-gray-100 overflow-hidden" id="comment">
          <Answer
            answerclass=""
            :question="set.question"
            :authid="set.authid"
            type="question"
            :article_id="null" />
        </div>

        <!-- CTA: задать свой вопрос (внутренняя перелинковка на /questions/add для SEO) -->
        <div class="mt-4 rounded-2xl border border-indigo-100 bg-indigo-50 px-4 py-5 text-center">
          <p class="text-sm font-semibold text-gray-900">Похожая ситуация?</p>
          <p class="text-xs text-gray-500 mt-1">Опишите свою — практикующие юристы ответят бесплатно.</p>
          <a :href="route('questions.add')"
             class="mt-3 inline-flex items-center gap-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-5 py-2.5 transition-colors">
            Задать вопрос юристу онлайн
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </a>
        </div>

        <!-- Collapsible: improve question -->
        <div class="mt-4 bg-white rounded-2xl border border-gray-100 overflow-hidden" id="guarantee">
          <button type="button" @click="showChecklist = !showChecklist"
                  class="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-600 hover:bg-gray-50">
            <span class="flex items-center gap-2">
              <span>Улучшить вопрос</span>
              <span class="text-xs px-2 py-0.5 rounded-full"
                    :class="percent == 100 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'">
                {{ percent }}%
              </span>
              <span class="text-xs text-gray-500 hidden sm:inline">{{ message }}</span>
            </span>
            <svg class="w-4 h-4 transition-transform duration-200 flex-shrink-0"
                 :class="{ 'rotate-180': showChecklist }"
                 xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m19 9-7 7-7-7"/>
            </svg>
          </button>

          <div v-show="showChecklist" class="px-6 pb-6 pt-2">
            <ol class="relative text-gray-500 border-l border-gray-200">
              <!--category-->
              <li class="mb-8 ml-6">
                <span v-if="set.question.uslugis_usl_name"
                      class="absolute flex items-center justify-center w-8 h-8 bg-green-200 rounded-full -left-4 ring-4 ring-white">
                  <svg class="w-3.5 h-3.5 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 16 12">
                    <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M1 5.917 5.724 10.5 15 1.5"/>
                  </svg>
                </span>
                <span v-else class="absolute flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full -left-4 ring-4 ring-white">1</span>
                <h3 class="font-medium leading-tight text-lg">Выберите категорию</h3>
                <p class="text-sm mt-0.5">{{ set.question.uslugis_usl_name ? 'Категория определена' : 'Отправим вопрос юристам с нужной специализацией' }}</p>
                <div v-if="set.question.user_id == authid || authid == 1 || !set.question.uslugis_usl_name" class="mt-3">
                  <select v-model="form.usluga" @change="setUsluga()"
                          class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2.5">
                    <option disabled value="0">Выберите один из вариантов</option>
                    <option v-for="option in uslugi" :key="option.id" :value="option.id">{{ option.usl_name }}</option>
                  </select>
                </div>
              </li>
              <!--region-->
              <li class="mb-8 ml-6">
                <span v-if="set.question.cities_title"
                      class="absolute flex items-center justify-center w-8 h-8 bg-green-200 rounded-full -left-4 ring-4 ring-white">
                  <svg class="w-3.5 h-3.5 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 16 12">
                    <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M1 5.917 5.724 10.5 15 1.5"/>
                  </svg>
                </span>
                <span v-else class="absolute flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full -left-4 ring-4 ring-white">2</span>
                <h3 class="font-medium leading-tight text-lg">Выберите регион</h3>
                <p class="text-sm mt-0.5">{{ set.question.cities_title ? 'Регион определен' : 'Привлекайте юристов из нужного региона (только для автора)' }}</p>
                <div class="mt-3" v-if="set.question.user_id == authid || authid == 1">
                  <button @click="openQuestionCity()" type="button"
                          class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2.5">
                    {{ set.question.cities_title ? set.question.cities_title : 'выберите регион' }}
                  </button>
                </div>
              </li>
              <!--profile-->
              <li class="mb-8 ml-6">
                <span v-if="set.question.phone"
                      class="absolute flex items-center justify-center w-8 h-8 bg-green-200 rounded-full -left-4 ring-4 ring-white">
                  <svg class="w-3.5 h-3.5 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 16 12">
                    <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M1 5.917 5.724 10.5 15 1.5"/>
                  </svg>
                </span>
                <span v-else class="absolute flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full -left-4 ring-4 ring-white">3</span>
                <h3 class="font-medium leading-tight text-lg">Заполните профиль</h3>
                <p class="text-sm mt-0.5">{{ set.question.phone ? 'Профиль заполнен' : 'Автор вопроса не рассказал о себе' }}</p>
                <div v-if="!set.question.phone && set.question.user_id == authid" class="mt-3">
                  <a :href="route('profile.edit')"
                     class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 inline-block p-2.5">
                    Заполнить профиль
                  </a>
                </div>
              </li>
              <!--share-->
              <li class="mb-2 ml-6">
                <span v-if="set.question.user_share"
                      class="absolute flex items-center justify-center w-8 h-8 bg-green-200 rounded-full -left-4 ring-4 ring-white">
                  <svg class="w-3.5 h-3.5 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 16 12">
                    <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M1 5.917 5.724 10.5 15 1.5"/>
                  </svg>
                </span>
                <span v-else class="absolute flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full -left-4 ring-4 ring-white">4</span>
                <h3 class="font-medium leading-tight text-lg">Поделитесь</h3>
                <p class="text-sm mt-0.5">{{ set.question.user_share ? 'Кто-то уже поделился' : 'Расскажите другим про Ваш вопрос и наш сервис' }}</p>
                <div class="mt-3 inline-flex items-center gap-2 bg-gray-50 border border-gray-300 rounded-lg p-2.5">
                  <span class="text-sm text-gray-600">Поделиться:</span>
                  <ShareButtons :bundle="set.question" :auth="set.auth" up class="!w-auto" />
                </div>
              </li>
            </ol>
          </div>
        </div>

      </div>
    </div>

    </main>
    <MainFooter />
    <Tracking :key="backendurl" :tracking="$page.props.tracking" :backendurl="set.backendurl" />
  </div>
</template>

<style scoped>
.fade-enter-active, .fade-leave-active { transition: opacity 0.5s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>
